import { BLOCKS, INLINES, MARKS } from "@contentful/rich-text-types";
import {
  Box,
  Heading,
  HeadingProps,
  Text,
  TextProps,
} from "@vinomofo/components";
import {
  Options,
  documentToReactComponents,
} from "@contentful/rich-text-react-renderer";
import { PropsWithChildren, ReactNode } from "react";

import { Document } from "@contentful/rich-text-types";
import { Link } from "../components/link";
import { RichText } from "@vinomofo/gatsby-types";
import parseHtml from "./contentful-html-to-richtext-converter";

type RichTextNode = { json: Document };

interface ComponentOptions {
  h1?: HeadingProps;
  h2?: HeadingProps;
  h3?: HeadingProps;
  h4?: HeadingProps;
  h5?: HeadingProps;
  h6?: HeadingProps;
  p?: TextProps;
  b?: any;
}

const StyledText = ({
  children,
  ...rest
}: PropsWithChildren<TextProps>): JSX.Element => {
  return (
    <Text fontSize={["16px", "18px", "20px"]} {...rest}>
      {children}
    </Text>
  );
};

const render = ({ json }: RichTextNode, opts?: Options): ReactNode => {
  return documentToReactComponents(json, opts);
};

export const richTextRenderer = (
  text: RichText,
  componentProps?: ComponentOptions
): ReactNode => {
  const Heading1 = (_, children) => {
    return (
      <Heading variant="xl" as="h1" {...componentProps?.h2}>
        {children}
      </Heading>
    );
  };

  const Heading2 = (_, children) => {
    return (
      <Heading variant="lg" as="h2" {...componentProps?.h2}>
        {children}
      </Heading>
    );
  };

  const Heading3 = (_, children) => {
    return (
      <Heading
        variant="md"
        as="h3"
        fontWeight="500"
        color="brand"
        {...componentProps?.h3}
      >
        {children}
      </Heading>
    );
  };

  const Heading4 = (_, children) => {
    return (
      <Heading
        variant="sm"
        as="h4"
        fontWeight="500"
        color="black"
        {...componentProps?.h3}
      >
        {children}
      </Heading>
    );
  };

  const Heading5 = (_, children) => {
    return (
      <Heading
        variant="xs"
        as="h5"
        fontWeight="500"
        color="black"
        {...componentProps?.h3}
      >
        {children}
      </Heading>
    );
  };

  const Heading6 = (_, children) => {
    return (
      <Heading
        variant="xxs"
        as="h6"
        fontWeight="500"
        color="black"
        {...componentProps?.h3}
      >
        {children}
      </Heading>
    );
  };

  const Paragraph = (_, children) => {
    return (
      <StyledText {...componentProps?.p} lineHeight="30px">
        {children}
      </StyledText>
    );
  };

  const BlockQuote = (_, children) => {
    return (
      <Box
        width="100%"
        borderLeft="4px solid"
        borderLeftColor="brand"
        paddingLeft="2em"
        as="blockquote"
      >
        {_.content[0].nodeType === "text" ? (
          <StyledText {...componentProps?.p} lineHeight="30px">
            {children}
          </StyledText>
        ) : (
          children
        )}
      </Box>
    );
  };

  const OrderedList = (_, children) => (
    <StyledText as="ol">{children}</StyledText>
  );

  const UnorderedList = (_, children) => (
    <StyledText as="ul">{children}</StyledText>
  );

  const BoldText = (text) => (
    <StyledText
      as="strong"
      fontWeight={600}
      color="black"
      {...componentProps?.b}
    >
      {text}
    </StyledText>
  );

  const EmphasizedText = (text) => <em>{text}</em>;
  const SuperScriptText = (text) => <sup>{text}</sup>;
  const SubScriptText = (text) => <sub>{text}</sub>;

  const InlineLink = (args, children) => {
    return (
      <Link to={args.data.uri} color="brand">
        {children}
      </Link>
    );
  };

  const EmbeddedAsset = (node) => {
    const { file, title, description } = node.data.target.fields;
    return (
      <Box textAlign="center">
        <img
          src={`https:${file.url}`}
          alt={description || title || 'Image'}
          style={{ maxWidth: "100%", height: "auto" }}
        />
      </Box>
    );
  }
  const EmbeddedEntry = (node) => {
    const { internalName, slug, image } = node.data.target.fields;

    if (!image || !image[0]) {
      return null;
    }

    const imageUrl = image[0].secure_url || `https:${image[0].url}`;
    const pageUrl = slug
      ? slug.startsWith('/')
        ? slug.toLowerCase() 
        : `/${slug.replace(/\s+/g, '-').toLowerCase()}`
      : null;

    return (
      <Box textAlign="center">
        {pageUrl ? (<a href={pageUrl} target="_blank" rel="noopener noreferrer">
          <img
            src={imageUrl}
            alt={internalName}
            style={{ maxWidth: "100%", height: "auto", cursor: "pointer" }}
          />
        </a>) : (
          <img
            src={imageUrl}
            alt={internalName}
            style={{ maxWidth: "100%", height: "auto" }}
          />
        )
        }
      </Box>
    );
  }
  const renderOptions = {
    renderMark: {
      [MARKS.BOLD]: BoldText,
      [MARKS.ITALIC]: EmphasizedText,
      [MARKS.SUPERSCRIPT]: SuperScriptText,
      [MARKS.SUBSCRIPT]: SubScriptText,
    },
    renderNode: {
      [BLOCKS.HEADING_1]: Heading1,
      [BLOCKS.HEADING_2]: Heading2,
      [BLOCKS.HEADING_3]: Heading3,
      [BLOCKS.HEADING_4]: Heading4,
      [BLOCKS.HEADING_5]: Heading5,
      [BLOCKS.HEADING_6]: Heading6,
      [BLOCKS.PARAGRAPH]: Paragraph,
      [BLOCKS.QUOTE]: BlockQuote,
      [BLOCKS.OL_LIST]: OrderedList,
      [BLOCKS.UL_LIST]: UnorderedList,
      [INLINES.HYPERLINK]: InlineLink,
      [BLOCKS.EMBEDDED_ASSET]: EmbeddedAsset,
      [BLOCKS.EMBEDDED_ENTRY]: EmbeddedEntry
    },
    renderText: (text: string | any) => {
      return text
        .replace("&nbsp;", " ")
        .split("\n")
        .reduce((children, textSegment, index) => {
          return [...children, index > 0 && <br key={index} />, textSegment];
        }, []);
    },
  };

  return render(text as RichTextNode, renderOptions);
};

export const richTextParserRenderer = (
  description: any,
  componentProps?: ComponentOptions
): ReactNode => {
  if (!description) return null;
  const { content } = description;
  const richTextOptions = componentProps
    ? componentProps
    : { b: { fontWeight: 600 } };

  if (Array.isArray(content) && content.length > 1) {
    return content.map((c_1, i) => {
      if (
        c_1.content[0]?.value &&
        c_1.content[0]?.value?.includes("layout-single-column")
      ) {
        return richTextRenderer(
          { json: parseHtml(c_1?.content[0]?.value as string) },
          richTextOptions
        );
      } else {
        return richTextRenderer({ json: c_1 }, richTextOptions);
      }
    });
  } else if (
    content[0]?.content[0]?.value &&
    content[0]?.content[0]?.value?.includes("layout-single-column")
  ) {
    return richTextRenderer(
      { json: parseHtml(content[0]?.content[0]?.value) },
      richTextOptions
    );
  } else {
    return richTextRenderer({ json: description }, richTextOptions);
  }
};
